import z from 'zod';

// FRAMES MASTER FILES
// POST -> /api/masterfiles/{project_id}/frame-masterfiles/
export const postFrameMasterFiles = {
  body: z
    .object({
      filename: z.string().nonempty({ error: 'File name is required' }),
      file_url: z.url().nonempty({ error: 'File url is required' }),
    })
    .array(),
  response: z.object({
    detail: z.string(),
  }),
  params: z.object({
    project_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postFrameMasterFilesSchema = z.object(postFrameMasterFiles);
export type PostFrameMasterFiles = z.infer<typeof postFrameMasterFilesSchema>;

// GET -> /api/masterfiles/{project_id}/frame-masterfiles/
const getFrameMasterFilesAll = {
  body: z.never(),
  response: z
    .object({
      id: z.number(),
      frame: z.string(),
      active_revision: z.string(),
      deleted_at: z.string().nullable().optional(),
      created_at: z.date(), // '2026-02-25T16:06:42.131Z'
      updated_at: z.date(), //'2026-02-25T16:06:42.131Z'
    })
    .array(),
  params: z.object({
    project_id: z.coerce.number(),
  }),
  query: z
    .object({
      deleted: z.boolean().optional(),
    })
    .optional(),
};
const getFrameMasterFilesAllSchema = z.object(getFrameMasterFilesAll);
export type GetFrameMasterFilesAll = z.infer<
  typeof getFrameMasterFilesAllSchema
>;

// GET -> /api/masterfiles/frame-masterfiles/{masterfile_id}/revisions/
const getFrameMasterFileRevisionsById = {
  body: z.never(),
  response: z
    .object({
      id: z.number(),
      revision_label: z.string(),
      file: z.string(),
      deleted_at: z.string().nullable().optional(),
      created_at: z.date(), // '2026-02-25T16:12:42.289Z'
    })
    .array(),
  params: z.object({
    masterfile_id: z.coerce.number(),
  }),
  query: z
    .object({
      deleted: z.boolean().optional(),
    })
    .optional(),
};
const getFrameMasterFileRevisionsByIdSchema = z.object(
  getFrameMasterFileRevisionsById,
);
export type GetFrameMasterFileRevisionsById = z.infer<
  typeof getFrameMasterFileRevisionsByIdSchema
>;

// POST -> /api/masterfiles/frame-masterfiles/files/ (This is a query. use as a GET request)
const postGetActivePresignedFrameMasterFilesById = {
  body: z.object({
    masterfile_ids: z.number().array(),
  }),
  response: z.record(
    z.string(), // dynamic keys: "1", "2", "123", ...
    z.string().nullable(), // value: URL string or null
  ),
  params: z.never(),
  query: z.never(),
};
const postGetActivePresignedFrameMasterFilesByIdSchema = z.object(
  postGetActivePresignedFrameMasterFilesById,
);
export type PostGetActivePresignedFrameMasterFilesById = z.infer<
  typeof postGetActivePresignedFrameMasterFilesByIdSchema
>;

// PARTS MASTER FILES
// POST -> /api/masterfiles/{project_id}/part-masterfiles/
export const postPartMasterFiles = {
  body: z
    .object({
      filename: z.string().nonempty({ error: 'File name is required' }),
      file_url: z.url().nonempty({ error: 'File url is required' }),
    })
    .array(),
  response: z.object({
    detail: z.string(),
  }),
  params: z.object({
    project_id: z.coerce.number(),
  }),
  query: z.never(),
};
const postPartMasterFilesSchema = z.object(postPartMasterFiles);
export type PostPartMasterFiles = z.infer<typeof postPartMasterFilesSchema>;

// GET -> /api/masterfiles/{project_id}/part-masterfiles/
const getPartMasterFilesAll = {
  body: z.never(),
  response: z
    .object({
      id: z.number(),
      part: z.number(),
      part_number: z.string(),
      active_revision: z.string(),
      deleted_at: z.string().nullable().optional(),
      created_at: z.date(), // '2026-02-25T16:06:42.131Z'
      updated_at: z.date(), //'2026-02-25T16:06:42.131Z'
    })
    .array(),
  params: z.object({
    project_id: z.coerce.number(),
  }),
  query: z
    .object({
      deleted: z.boolean().optional(),
    })
    .optional(),
};
const getPartMasterFilesAllSchema = z.object(getPartMasterFilesAll);
export type GetPartMasterFilesAll = z.infer<typeof getPartMasterFilesAllSchema>;

// GET -> /api/masterfiles/part-masterfiles/{masterfile_id}/revisions/
const getPartMasterFileRevisionsById = {
  body: z.never(),
  response: z
    .object({
      id: z.number(),
      revision_label: z.string(),
      file: z.string(),
      deleted_at: z.string().nullable().optional(),
      created_at: z.date(), // '2026-02-25T16:12:42.289Z'
    })
    .array(),
  params: z.object({
    masterfile_id: z.coerce.number(),
  }),
  query: z
    .object({
      deleted: z.boolean().optional(),
    })
    .optional(),
};
const getPartMasterFileRevisionsByIdSchema = z.object(
  getPartMasterFileRevisionsById,
);
export type GetPartMasterFileRevisionsById = z.infer<
  typeof getPartMasterFileRevisionsByIdSchema
>;

// POST -> /api/masterfiles/part-masterfiles/files/ (This is a query. use as a GET request)
const postGetActivePresignedPartMasterFilesById = {
  body: z.object({
    masterfile_ids: z.number().array(),
  }),
  response: z.record(
    z.string(), // dynamic keys: "1", "2", "123", ...
    z.string().nullable(), // value: URL string or null
  ),
  params: z.never(),
  query: z.never(),
};
const postGetActivePresignedPartMasterFilesByIdSchema = z.object(
  postGetActivePresignedPartMasterFilesById,
);
export type PostGetActivePresignedPartMasterFilesById = z.infer<
  typeof postGetActivePresignedPartMasterFilesByIdSchema
>;

const bulkActionSkippedItem = z.object({
  id: z.number(),
  reason: z.string(),
});

const masterfileBulkActionBody = z.object({
  masterfile_ids: z.number().array().min(1),
});

const masterfileBulkActionParams = z.object({
  project_id: z.coerce.number(),
});

const masterfileRevisionActionParams = z.object({
  project_id: z.coerce.number(),
  masterfile_id: z.coerce.number(),
  revision_id: z.coerce.number(),
});

const masterfileRevisionActionResponse = z.object({
  detail: z.string(),
  active_revision: z.string().nullable(),
});

const deleteFrameMasterfiles = {
  body: masterfileBulkActionBody,
  response: z.object({
    detail: z.string(),
    deleted_ids: z.number().array(),
    skipped: bulkActionSkippedItem.array(),
  }),
  params: masterfileBulkActionParams,
  query: z.never(),
};
const deleteFrameMasterfilesSchema = z.object(deleteFrameMasterfiles);
export type DeleteFrameMasterfiles = z.infer<
  typeof deleteFrameMasterfilesSchema
>;

const restoreFrameMasterfiles = {
  body: masterfileBulkActionBody,
  response: z.object({
    detail: z.string(),
    restored_ids: z.number().array(),
    skipped: bulkActionSkippedItem.array(),
  }),
  params: masterfileBulkActionParams,
  query: z.never(),
};
const restoreFrameMasterfilesSchema = z.object(restoreFrameMasterfiles);
export type RestoreFrameMasterfiles = z.infer<
  typeof restoreFrameMasterfilesSchema
>;

const deleteFrameMasterfileRevision = {
  body: z.never(),
  response: masterfileRevisionActionResponse,
  params: masterfileRevisionActionParams,
  query: z.never(),
};
const deleteFrameMasterfileRevisionSchema = z.object(
  deleteFrameMasterfileRevision,
);
export type DeleteFrameMasterfileRevision = z.infer<
  typeof deleteFrameMasterfileRevisionSchema
>;

const restoreFrameMasterfileRevision = {
  body: z.never(),
  response: masterfileRevisionActionResponse,
  params: masterfileRevisionActionParams,
  query: z.never(),
};
const restoreFrameMasterfileRevisionSchema = z.object(
  restoreFrameMasterfileRevision,
);
export type RestoreFrameMasterfileRevision = z.infer<
  typeof restoreFrameMasterfileRevisionSchema
>;

const deletePartMasterfiles = {
  body: masterfileBulkActionBody,
  response: z.object({
    detail: z.string(),
    deleted_ids: z.number().array(),
    skipped: bulkActionSkippedItem.array(),
  }),
  params: masterfileBulkActionParams,
  query: z.never(),
};
const deletePartMasterfilesSchema = z.object(deletePartMasterfiles);
export type DeletePartMasterfiles = z.infer<typeof deletePartMasterfilesSchema>;

const restorePartMasterfiles = {
  body: masterfileBulkActionBody,
  response: z.object({
    detail: z.string(),
    restored_ids: z.number().array(),
    skipped: bulkActionSkippedItem.array(),
  }),
  params: masterfileBulkActionParams,
  query: z.never(),
};
const restorePartMasterfilesSchema = z.object(restorePartMasterfiles);
export type RestorePartMasterfiles = z.infer<
  typeof restorePartMasterfilesSchema
>;

const deletePartMasterfileRevision = {
  body: z.never(),
  response: masterfileRevisionActionResponse,
  params: masterfileRevisionActionParams,
  query: z.never(),
};
const deletePartMasterfileRevisionSchema = z.object(
  deletePartMasterfileRevision,
);
export type DeletePartMasterfileRevision = z.infer<
  typeof deletePartMasterfileRevisionSchema
>;

const restorePartMasterfileRevision = {
  body: z.never(),
  response: masterfileRevisionActionResponse,
  params: masterfileRevisionActionParams,
  query: z.never(),
};
const restorePartMasterfileRevisionSchema = z.object(
  restorePartMasterfileRevision,
);
export type RestorePartMasterfileRevision = z.infer<
  typeof restorePartMasterfileRevisionSchema
>;
